import React, { useState, useEffect } from "react";
import Header from "components/Header";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { Print, Edit, Search, Download } from "@mui/icons-material";
import api from "services/api";
import * as XLSX from "xlsx";
import {
  Box,
  Button,
  useTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  InputBase,
  IconButton,
  Chip,
} from "@mui/material";

const Factures = () => {
  const theme = useTheme();
  const [consommations, setConsommations] = useState([]);
  const [filteredConsommations, setFilteredConsommations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchConsommations = async () => {
    try {
      setIsLoading(true);

      const response = await api.get("/api/consommations");

      const data = response.data || [];
      data.sort((a, b) => b.id - a.id);

      console.log("Data:", data);

      setConsommations(data);
      setFilteredConsommations(data);
    } catch (error) {
      console.error("Failed to fetch consommations:", error);
    } finally {
      setIsLoading(false);
    }
  };


  useEffect(() => {
    fetchConsommations();
  }, []);


  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    setFilteredConsommations(
      consommations.filter((row) =>
        [
          "waterBillRefNumber",
          "selectedDate",
          "firstName",
          "lastName",
          "phoneNumber",
        ].some((field) => row[field]?.toString().toLowerCase().includes(query))
      )
    );
  };

  const handleExport = () => {
    // Add village column and prepare data for export
    const preparedData = consommations.map(row => ({
      Village: row.waterBillRefNumber?.slice(0, 5) || "Unknown", // Extract first 5 letters as Village
      ClientId: row.clientId || "No Client ID", // Ensure ClientId is included and handle missing values
      ...row, // Include all existing columns
    }));

    // Group data by village
    const groupedByVillage = preparedData.reduce((acc, row) => {
      const village = row.Village;
      if (!acc[village]) {
        acc[village] = [];
      }
      acc[village].push(row);
      return acc;
    }, {});

    // Prepare workbook and worksheets
    const wb = XLSX.utils.book_new();
    for (const village in groupedByVillage) {
      const rows = groupedByVillage[village];

      // Create worksheet for each village
      const ws = XLSX.utils.json_to_sheet(rows);

      // Optionally adjust column widths for better readability
      ws['!cols'] = Object.keys(rows[0] || {}).map(() => ({ width: 20 }));

      // Add worksheet to the workbook
      XLSX.utils.book_append_sheet(wb, ws, village);
    }

    // Export workbook to file
    XLSX.writeFile(wb, "Consommations_par_Village.xlsx");
  };

  const handlePrint = (row) => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
		<html>
		  <head>
			<title>Receipt</title>
			<style>
			  body {
				font-family: Arial, sans-serif;
				margin: 20px;
				padding: 20px;
				color: #333;
				background-color: #f5f5f5;
			  }
			  h1 {
				text-align: center;
				color: #444;
			  }
			  .container {
				display: flex;
				flex-wrap: wrap;
				justify-content: center;
				margin-top: 20px;
			  }
			  .column {
				width: 45%;
				padding: 20px;
				background-color: #fff;
				box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
				border-radius: 8px;
				margin: 10px;
				text-align: center;
			  }
			  .column h2 {
				border-bottom: 2px solid #444;
				padding-bottom: 10px;
				color: #444;
			  }
			  .column p {
				margin: 10px 0;
				font-size: 14px;
			  }
			  .footer {
				text-align: center;
				margin-top: 20px;
				font-size: 12px;
				color: #777;
			  }
			</style>
		  </head>
		  <body>
			<h1>Reçu</h1>
			<div class="container">
			  <div class="column">
				<h2>Information du client</h2>
				<p>Propriétaire: ${row.firstName} ${row.lastName}</p>
				<p>Commune: ${row.state}</p>
				<p>Numéro de Téléphone: ${row.phoneNumber}</p>
			  </div>
			  <div class="column">
				<h2>Référence compteur</h2>
				<p>ID: ${row.id}</p>
				<p>Référence Facture Eau: ${row.waterBillRefNumber}</p>
				<p>Type Branchement: ${row.typeBranchement}</p>
			  </div>
			  <div class="column">
				<h2>Consommation</h2>
				<p>Date: ${row.selectedDate}</p>
				<p>Index Actuel: ${row.currentIndex}</p>
				<p>Index Précédent: ${row.previousIndex}</p>
			  </div>
			  <div class="column">
				<h2>Détails paiement</h2>
				<p>Consommation: ${row.consommation}</p>
				<p>Montant Payé: ${row.paidAmount}</p>
				<p>Statut de Paiement: ${row.paymentStatus}</p>
			  </div>
			</div>
			<div class="footer">
			  <p>Merci pour votre paiement. COPIFOR DE DAROU NGARAF.</p>
			</div>
		  </body>
		</html>
	  `);
    printWindow.document.close();
    printWindow.print();
  };


  const handleEdit = (row) => {
    // Check if row.duePaymentDate is defined and is a string
    const duePaymentDate = row.duePaymentDate
      ? row.duePaymentDate.split("T")[0]
      : ""; // Provide a default value if needed

    // Update the state with the formatted date
    setSelectedRow({
      ...row,
      duePaymentDate: duePaymentDate,
    });

    // Open the edit dialog
    setOpenEditDialog(true);
  };


  const handleEditChange = (e) => {
    const { name, value } = e.target;
    let updatedRow = { ...selectedRow, [name]: value };

    if (["currentIndex", "previousIndex", "price"].includes(name)) {
      updatedRow[name] = parseFloat(value);
    }

    if (["currentIndex", "previousIndex", "price"].includes(name)) {
      updatedRow = {
        ...updatedRow,
        consommation: updatedRow.currentIndex - updatedRow.previousIndex,
        paidAmount:
          (updatedRow.currentIndex - updatedRow.previousIndex) *
          updatedRow.price,
        duePayment:
          (updatedRow.currentIndex - updatedRow.previousIndex) *
          updatedRow.price,
      };
    }

    if (["paidDate", "duePaymentDate"].includes(name)) {
      const dateParts = value.split("-");
      if (dateParts.length === 3) {
        const formattedDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`; // Convert to YYYY-MM-DD
        updatedRow = {
          ...updatedRow,
          [name]: formattedDate,
        };

        if (name === "paidDate" && value) {
          updatedRow.paymentStatus = "payé";
        }
      }

      // Log paidDate and duePaymentDate
      console.log(`Updated ${name}:`, updatedRow[name]);
    }


    if (
      ["paidDate", "duePaymentDate", "latePayment", "finePrice"].includes(name) ||
      ["paidDate", "duePaymentDate"].includes(name)
    ) {
      const paidDateStr = updatedRow.paidDate || "";
      const duePaymentDateStr = updatedRow.duePaymentDate || "";

      const parseDate = (dateStr) => {
        const [day, month, year] = dateStr.split("-").map(Number);
        return new Date(year, month - 1, day);
      };

      const paidDate = parseDate(paidDateStr);
      const duePaymentDate = parseDate(duePaymentDateStr);

      const differenceMs = paidDate.getTime() - duePaymentDate.getTime();
      const differenceDays = Math.floor(differenceMs / (1000 * 60 * 60 * 24));
      const latePayment = differenceDays < 0 ? 0 : differenceDays;

      updatedRow = {
        ...updatedRow,
        latePayment,
        fine: latePayment * (updatedRow.finePrice || 0),
      };

      // Log paidDate, duePaymentDate, and latePayment
      console.log("Paid Date:", paidDate);
      console.log("Due Payment Date:", duePaymentDate);
      console.log("Late Payment:", latePayment);
    }

    setSelectedRow(updatedRow);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [day, month, year] = dateStr.split("-").map(Number);
    return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
  };


  const handleSubmitEdit = async () => {
    if (!selectedRow?.paidAmount && selectedRow?.paidAmount !== 0) {
      alert("Veuillez entrer un montant payé.");
      return;
    }

    console.log("Submitting the following data to the backend:", selectedRow);

    try {
      await api.put(`/api/consommations/${selectedRow.id}`, selectedRow);

      await fetchConsommations();

      setOpenEditDialog(false);
      setSelectedRow(null);
    } catch (error) {
      console.error("Failed to update consommation:", error);
    }
  };


  const columns = [
    { field: "id", headerName: "ID", width: 40 },
    { field: "selectedDate", headerName: "Date", width: 90 },
    { field: "firstName", headerName: "Nom Propriétaire", width: 150 },
    { field: "lastName", headerName: "Prénom Propriétaire", width: 150 },
    { field: "phoneNumber", headerName: "Téléphone", width: 150, valueGetter: (params) => params.row.phoneNumber || "-" },
    { field: "state", headerName: "Commune", width: 120 },
    { field: "typeBranchement", headerName: "Branchement", width: 100 },
    { field: "currentIndex", headerName: "Index Actuel", width: 90 },
    { field: "previousIndex", headerName: "Index Précédent", width: 120 },
    { field: "consommation", headerName: "Consommation", width: 100 },
    {
      field: "waterBillRefNumber",
      headerName: "Référence Facture Eau",
      width: 160,
    },
    { field: "paidAmount", headerName: "Montant Payé", width: 120 },
    { field: "duePaymentDate", headerName: "Date Paiement Dû", width: 120 },
    { field: "paidDate", headerName: "Date Paiement", width: 120, valueGetter: (params) => params.row.paidDate || "-" },
    { field: "finePrice", headerName: "Prix Amende", width: 120 },
    { field: "latePayment", headerName: "Retard Paiement", width: 120 },
    { field: "fine", headerName: "Amende", width: 130 },
    {
      field: "paymentStatus",
      headerName: "Statut de Paiement",
      width: 160,
      renderCell: (params) => {
        const isPaid = params.value === "payé";

        return (
          <Chip
            label={isPaid ? "Payé" : "Impayé"}
            color={isPaid ? "success" : "error"}
            size="small"
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      type: "actions",
      width: 140,
      headerAlign: "center",
      align: "center",

      getActions: (params) => [
        <GridActionsCellItem
          icon={
            <Print
              sx={{
                color: "#4caf50",
                fontSize: "1.4rem",
              }}
            />
          }
          label="Imprimer"
          onClick={() => handlePrint(params.row)}
          sx={{
            "&:hover": {
              backgroundColor: "rgba(76, 175, 80, 0.12)",
            },
            borderRadius: "0.5rem",
          }}
        />,

        <GridActionsCellItem
          icon={
            <Edit
              sx={{
                color: "#2196f3",
                fontSize: "1.4rem",
              }}
            />
          }
          label="Modifier"
          onClick={() => handleEdit(params.row)}
          sx={{
            "&:hover": {
              backgroundColor: "rgba(33, 150, 243, 0.12)",
            },
            borderRadius: "0.5rem",
          }}
        />,
      ],
    }
  ];

  return (
    <Box
      m="1.5rem 2.5rem"
      sx={{
        overflowX: "hidden",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap="20px"
        sx={{
          flexDirection: {
            xs: "column",
            md: "row",
          },
          alignItems: {
            xs: "flex-start",
            md: "center",
          },
        }}
      >
        <Header title="FACTURES" subtitle="Liste des factures des clients" />

        <Box
          display="flex"
          alignItems="center"
          gap="12px"
          sx={{
            width: {
              xs: "100%",
              md: "auto",
            },
            flexWrap: "wrap",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            sx={{
              width: {
                xs: "100%",
                sm: "280px",
              },
              backgroundColor: theme.palette.background.alt,
              borderRadius: "0.75rem",
              px: 1,
            }}
          >
            <InputBase
              placeholder="Rechercher…"
              value={searchQuery}
              onChange={handleSearch}
              sx={{
                ml: 1,
                flex: 1,
                width: "100%",
              }}
              inputProps={{ "aria-label": "search" }}
            />
            <IconButton sx={{ p: 1 }} aria-label="search">
              <Search />
            </IconButton>
          </Box>

          <Button
            variant="contained"
            color="primary"
            onClick={handleExport}
            startIcon={<Download />}
            sx={{
              width: {
                xs: "100%",
                sm: "auto",
              },
              minHeight: "48px",
              whiteSpace: "nowrap",
            }}
          >
            Exporter Excel
          </Button>
        </Box>
      </Box>

      <Box
        mt="40px"
        height="75vh"
        // sx={{
        //   width: "100%",
        //   maxWidth: "100%",
        //   minWidth: 0,
        //   overflowX: "auto",
        //   overflowY: "hidden",

        //   "& .MuiDataGrid-root": {
        //     border: "none",
        //     borderRadius: "1rem",
        //     minWidth: {
        //       xs: "1800px",
        //       md: "100%",
        //     },
        //     backgroundColor: theme.palette.background.alt,
        //   },

        //   "& .MuiDataGrid-cell": {
        //     borderBottom: "1px solid rgba(255, 255, 255, 0.14)",
        //     color: theme.palette.secondary[100],
        //     fontWeight: "600",
        //   },

        //   "& .MuiDataGrid-row": {
        //     borderBottom: "1px solid rgba(255, 255, 255, 0.14)",
        //   },

        //   "& .MuiDataGrid-columnHeaders": {
        //     backgroundColor: theme.palette.background.alt,
        //     color: theme.palette.secondary[100],
        //     borderBottom: "1px solid rgba(255, 255, 255, 0.18)",
        //   },

        //   "& .MuiDataGrid-columnHeaderTitle": {
        //     fontWeight: "700",
        //   },

        //   "& .MuiDataGrid-virtualScroller": {
        //     backgroundColor: theme.palette.background.alt,
        //   },

        //   "& .MuiDataGrid-footerContainer": {
        //     backgroundColor: theme.palette.background.alt,
        //     color: theme.palette.secondary[100],
        //     borderTop: "1px solid rgba(255, 255, 255, 0.14)",
        //   },

        //   "& .MuiTablePagination-root": {
        //     backgroundColor: theme.palette.background.alt,
        //     color: theme.palette.secondary[100],
        //   },

        //   "& .MuiDataGrid-iconSeparator": {
        //     color: theme.palette.secondary[200],
        //   },

        //   "& .MuiDataGrid-menuIconButton": {
        //     color: theme.palette.secondary[100],
        //   },

        //   "& .MuiDataGrid-sortIcon": {
        //     color: theme.palette.secondary[100],
        //   },
        // }}
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflowX: "auto",
          overflowY: "hidden",

          "& .MuiDataGrid-root": {
            border: "none",
            minWidth: {
              xs: "1800px",
              md: "100%",
            },
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid rgba(255, 255, 255, 0.14)",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderBottom: "1px solid rgba(255, 255, 255, 0.18)",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: theme.palette.primary.light,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderTop: "1px solid rgba(255, 255, 255, 0.14)",
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${theme.palette.secondary[200]} !important`,
          },
        }}
      >
        <DataGrid
          loading={isLoading}
          rows={filteredConsommations}
          columns={columns.map((col) => ({
            headerAlign: "center",
            align: "center",
            ...col,
          }))}
          localeText={{
            noRowsLabel: searchQuery
              ? "Aucun résultat trouvé pour cette recherche."
              : "Aucune donnée disponible.",
          }}
        />
      </Box>

      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Modifier Consommation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Utilisez le formulaire ci-dessous pour modifier les détails de la
            consommation.
          </DialogContentText>
          <TextField
            margin="dense"
            name="firstName"
            label="Nom Propriétaire"
            fullWidth
            value={selectedRow?.firstName || ""}
            onChange={handleEditChange}
            disabled
          />
          <TextField
            margin="dense"
            name="lastName"
            label="Prénom Propriétaire"
            fullWidth
            value={selectedRow?.lastName || ""}
            onChange={handleEditChange}
            disabled
          />
          <TextField
            margin="dense"
            name="phoneNumber"
            label="Téléphone"
            fullWidth
            value={selectedRow?.phoneNumber || ""}
            onChange={handleEditChange}
          />
          <TextField
            margin="dense"
            name="state"
            label="Commune"
            fullWidth
            value={selectedRow?.state || ""}
            onChange={handleEditChange}
            disabled
          />
          <TextField
            margin="dense"
            name="typeBranchement"
            label="Type Branchement"
            fullWidth
            value={selectedRow?.typeBranchement || ""}
            onChange={handleEditChange}
            disabled
          />
          <TextField
            margin="dense"
            name="waterBillRefNumber"
            label="Référence Facture Eau"
            fullWidth
            value={selectedRow?.waterBillRefNumber || ""}
            onChange={handleEditChange}
            disabled
          />
          <TextField
            margin="dense"
            name="currentIndex"
            label="Index Actuel"
            type="number"
            fullWidth
            value={selectedRow?.currentIndex || ""}
            onChange={handleEditChange}
          />
          <TextField
            margin="dense"
            name="previousIndex"
            label="Index Précédent"
            type="number"
            fullWidth
            value={selectedRow?.previousIndex || 0}
            onChange={handleEditChange}
          />
          <TextField
            margin="dense"
            name="consommation"
            label="Consommation"
            type="number"
            fullWidth
            value={selectedRow?.consommation || ""}
            disabled
          />
          <TextField
            margin="dense"
            name="price"
            label="Prix"
            type="number"
            fullWidth
            value={selectedRow?.price || ""}
            onChange={handleEditChange}
            disabled
          />
          <TextField
            margin="dense"
            name="duePayment"
            label="Montant à Payé"
            type="number"
            fullWidth
            value={selectedRow?.duePayment || ""}
            disabled
          />
          <TextField
            margin="dense"
            name="paidAmount"
            label="Montant Payé"
            type="number"
            fullWidth
            value={selectedRow?.paidAmount || "0"}
            onChange={handleEditChange}
          />
          <TextField
            margin="dense"
            name="finePrice"
            label="Prix Amende"
            type="number"
            fullWidth
            value={selectedRow?.finePrice || ""}
            onChange={handleEditChange}
            disabled
          />
          <TextField
            margin="dense"
            name="paidDate"
            label="Date Paiement"
            type="date"
            fullWidth
            value={selectedRow?.paidDate ? formatDate(selectedRow.paidDate) : ""}
            onChange={handleEditChange}
            placeholder="DD-MM-YYYY"
            InputLabelProps={{
              shrink: true,
            }}
          />

          <TextField
            margin="dense"
            name="duePaymentDate"
            label="Date Limite Paiement"
            type="date"
            fullWidth
            value={selectedRow?.duePaymentDate ? formatDate(selectedRow.duePaymentDate) : ""}
            onChange={handleEditChange}
            disabled
            placeholder="DD-MM-YYYY"
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            margin="dense"
            name="latePayment"
            label="Retard Paiement"
            type="number"
            fullWidth
            value={selectedRow?.latePayment || "0"}
            onChange={handleEditChange}
            disabled
          />
          <TextField
            margin="dense"
            name="fine"
            label="Amende"
            type="number"
            fullWidth
            value={selectedRow?.fine || "0"}
            onChange={handleEditChange}
            disabled
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Statut de Paiement</InputLabel>
            <Select
              name="paymentStatus"
              value={selectedRow?.paymentStatus || ""}
              onChange={handleEditChange}
            >
              <MenuItem value="payé">Payé</MenuItem>
              <MenuItem value="impayé">Impayé</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Annuler</Button>
          <Button onClick={handleSubmitEdit}>Enregistrer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Factures;